import React, { useState, useContext, useEffect, useMemo } from "react";
import { CartContext } from "../../contexts/CartContext";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import "./ProductPopupPage.css";
import AIS140Availability from "../../contexts/AIS140Availabilities";
import { FaTruck, FaShieldAlt } from "react-icons/fa";

const ProductPopupPage = ({ device }) => {
  const { cartItems, addToCart, updateCartItem, cartLoaded } =
    useContext(CartContext);

  const navigate = useNavigate();

  const [selectedRelayer, setSelectedRelayer] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(device?.image?.[1] || "");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const existingCartItem = cartItems.find(
    (item) => Number(item?.device?.id) === Number(device?.id)
  );

  const isProductInCart = !!existingCartItem;

  const isAIS = device?.name?.toUpperCase().includes("AIS");

  const selectedCityInfo = AIS140Availability[selectedState]?.find(
    (city) => city.name === selectedCity
  );

  const subscriptionOptions = useMemo(() => {
    if (!device?.isSubscription) return [];

    const options = [];

    if (device.isMonthlySubscription) {
      options.push({
        value: "monthly",
        amount: Number(device.subscriptionMonthlyAmt || 0),
        label:
          Number(device.subscriptionMonthlyAmt) === 0
            ? "1 Month Included"
            : `1 Month (+₹${device.subscriptionMonthlyAmt})`,
      });
    }

    if (device.isYearlySubscription) {
      options.push({
        value: "yearly",
        amount: Number(device.subscriptionYearlyAmt || 0),
        label:
          Number(device.subscriptionYearlyAmt) === 0
            ? "1 Year Included"
            : `1 Year (+₹${device.subscriptionYearlyAmt})`,
      });
    }

    if (device.isTwoYearSubscription) {
      options.push({
        value: "two_year",
        amount: Number(device.subscriptionTwoYearAmt || 0),
        label:
          Number(device.subscriptionTwoYearAmt) === 0
            ? "2 Years Included"
            : `2 Years (+₹${device.subscriptionTwoYearAmt})`,
      });
    }

    return options;
  }, [device]);

  const networkOptions = useMemo(() => {
    if (!device?.isNetwork) return [];

    const options = [];

    if (Number(device.network2gAmt) !== -1) {
      options.push({
        value: "2G",
        amount: Number(device.network2gAmt || 0),
      });
    }

    if (Number(device.network4gAmt) !== -1) {
      options.push({
        value: "4G",
        amount: Number(device.network4gAmt || 0),
      });
    }

    return options;
  }, [device]);

  useEffect(() => {
    if (device?.image?.length) {
      setSelectedImage(device.image[1] || device.image[0]);
    }
  }, [device]);

  useEffect(() => {
    if (!cartLoaded || !device) return;

    if (existingCartItem) {
      setQuantity(existingCartItem.quantity || 1);
      setSelectedNetwork(existingCartItem.network || null);
      setSelectedRelayer(!!existingCartItem.relayer);
      setSelectedSubscription(existingCartItem.subscription || null);
      setSelectedState(existingCartItem.state || "");
      setSelectedCity(existingCartItem.city || "");
    }
  }, [cartLoaded, device, existingCartItem]);

  useEffect(() => {
    if (!device || isProductInCart) return;

    setSelectedRelayer(
      device.isRelay && Number(device.relayAmt) === 0
    );
  }, [device, isProductInCart]);

  useEffect(() => {
    if (!networkOptions.length || isProductInCart) return;

    const includedOption = networkOptions.find((item) => item.amount === 0);
    setSelectedNetwork(includedOption?.value || networkOptions[0]?.value);
  }, [networkOptions, isProductInCart]);

  useEffect(() => {
    if (!subscriptionOptions.length || isProductInCart) return;

    const includedPlan = subscriptionOptions.find((item) => item.amount === 0);
    setSelectedSubscription(includedPlan?.value || subscriptionOptions[0]?.value);
  }, [subscriptionOptions, isProductInCart]);

  const round2 = (n) => Math.round(n * 100) / 100;

  const pricingCalculation = useMemo(() => {
    if (!device) {
      return {
        discountedPrice: 0,
        singleItemDiscountedPrice: 0,
        totalPrice: 0,
        savings: 0,
        currentPrice: 0,
        minMRP: 0,
        maxMRP: 0,
        discountPercent: 0,
        tax: 0,
        baseAmount: 0,
        optionsTotal: 0,
      };
    }

    const gstPercent = Number(device.gstPercent || 18);
    const discount = Number(device.discount || 0);
    const currentDeviceAmount = Number(device.amount || 0);
    const safeAmount = (v) => (Number(v) > 0 ? Number(v) : 0);

    let addons = 0;

    if (selectedNetwork === "2G" && Number(device.network2gAmt) > 0) {
      addons += Number(device.network2gAmt);
    }

    if (selectedNetwork === "4G" && Number(device.network4gAmt) > 0) {
      addons += Number(device.network4gAmt);
    }

    if (selectedRelayer && Number(device.relayAmt) > 0) {
      addons += Number(device.relayAmt);
    }

    const selectedSubscriptionOption = subscriptionOptions.find(
      (option) => option.value === selectedSubscription
    );

    addons += Number(selectedSubscriptionOption?.amount || 0);

    const currentTax = round2(currentDeviceAmount * (gstPercent / 100));
    const currentPrice = round2(currentDeviceAmount + currentTax + addons);

    let discountedDeviceAmount = currentDeviceAmount;

    if (discount > 0) {
      discountedDeviceAmount =
        discountedDeviceAmount * (1 - discount / 100);
    }

    const unitBase = discountedDeviceAmount + addons;

    const taxOnUnitBase = round2(
      round2(discountedDeviceAmount) * (gstPercent / 100)
    );

    const singleItemDiscountedPrice = round2(unitBase + taxOnUnitBase);

    const subtotal = unitBase * quantity;

    const subtotalWithQty = round2(discountedDeviceAmount * quantity);

    const totalTax = round2(subtotalWithQty * (gstPercent / 100));

    const discountedPrice = round2(subtotal + totalTax);

    const minMRP = round2(
      Number(device.amount || 0) +
        round2(Number(device.amount || 0) * (gstPercent / 100))
    );

    const maxNetwork = Math.max(
      safeAmount(device.network2gAmt),
      safeAmount(device.network4gAmt)
    );

    const maxSubscription = Math.max(
      device.isMonthlySubscription ? safeAmount(device.subscriptionMonthlyAmt) : 0,
      device.isYearlySubscription ? safeAmount(device.subscriptionYearlyAmt) : 0,
      device.isTwoYearSubscription ? safeAmount(device.subscriptionTwoYearAmt) : 0
    );

    const maxRelay = safeAmount(device.relayAmt);

    const maxMRP = round2(
      minMRP + maxNetwork + maxSubscription + maxRelay
    );

    return {
      discountedPrice,
      singleItemDiscountedPrice,
      totalPrice: discountedPrice,
      savings: round2((currentPrice - singleItemDiscountedPrice) * quantity),
      currentPrice,
      minMRP,
      maxMRP,
      discountPercent: discount,
      tax: totalTax,
      baseAmount: round2(discountedDeviceAmount + addons),
      optionsTotal: addons,
    };
  }, [
    device,
    selectedNetwork,
    selectedRelayer,
    selectedSubscription,
    quantity,
    subscriptionOptions,
  ]);

  const isBuyDisabled = isAIS
    ? !selectedState || !selectedCity || !selectedCityInfo?.availability
    : false;

  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setSelectedCity("");
  };

  const handleAddToCart = async () => {
    if (isProcessing || !device) return;

    if (isAIS && (!selectedState || !selectedCity)) {
      toast.error("Please select your state and city");
      return;
    }

    setIsProcessing(true);

    try {
      const cartData = {
        deviceId: device.id,
        product: { selectedDevice: device },
        quantity,
        price: pricingCalculation.singleItemDiscountedPrice,
        totalAmount: pricingCalculation.totalPrice,
        name: device.name,
        model: device.model,
        discount: device.discount || 0,
        network: selectedNetwork,
        isRelay: device.isRelay,
        relayer: selectedRelayer,
        subscription: selectedSubscription,
        state: selectedState,
        city: selectedCity,
      };

      if (isProductInCart) {
        await updateCartItem(existingCartItem, cartData);
      } else {
        await addToCart(cartData);
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBuyNow = async () => {
    await handleAddToCart();
    navigate("/cart");
  };

  if (!cartLoaded) return <div>Loading product...</div>;

  return (
    <div className="ProductPopupPage-container">
      <div className="ProductPopupPage-header">
        <div className="ProductPopupPage-image-wrapper">
          <img
            src={selectedImage || "/images/placeholder-product.png"}
            alt={device.name}
            className="ProductPopupPage-main-image"
            onError={(e) => (e.target.src = "/images/placeholder-product.png")}
          />

          <div className="ProductPopupPage-thumbnails">
            {device?.image?.slice(1).map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`thumb-${index + 1}`}
                className={`ProductPopupPage-thumb ${
                  selectedImage === img ? "active" : ""
                }`}
                onClick={() => setSelectedImage(img)}
              />
            ))}
          </div>
        </div>

        <div className="ProductPopupPage-details">
          <h2 className="ProductPopupPage-title">
            {device.name} {device.model}
          </h2>
          <p className="ProductPopupPage-subtitle">{device.description}</p>
        </div>
      </div>

      {device?.isRelay && (
        <div className="product-popup-extra-card">
          <h3 className="product-popup-heading">Relayer Option:</h3>

          <div className="product-popup-btns">
            {Number(device.relayAmt) === 0 ? (
              <button className="product-popup-btn active">
                Relay Included
              </button>
            ) : (
              <>
                <button
                  className={`product-popup-btn ${
                    !selectedRelayer ? "active" : ""
                  }`}
                  onClick={() => setSelectedRelayer(false)}
                >
                  Without Relay
                </button>

                <button
                  className={`product-popup-btn ${
                    selectedRelayer ? "active" : ""
                  }`}
                  onClick={() => setSelectedRelayer(true)}
                >
                  With Relay (+₹{device.relayAmt})
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {device?.isNetwork &&
        !(Number(device.network2gAmt) === -1 && Number(device.network4gAmt) === -1) && (
          <div className="product-popup-extra-card">
            <h3 className="product-popup-heading">Network Options:</h3>

            <div className="product-popup-btns">
              {networkOptions.map((network) => (
                <button
                  key={network.value}
                  className={`product-popup-btn ${
                    selectedNetwork === network.value ? "active" : ""
                  }`}
                  onClick={() => setSelectedNetwork(network.value)}
                >
                  {network.value}
                  {network.amount === 0
                    ? " Included"
                    : ` (+₹${network.amount})`}
                </button>
              ))}
            </div>
          </div>
        )}

      {device?.isSubscription && subscriptionOptions.length > 0 && (
        <div className="product-popup-extra-card">
          <h3 className="product-popup-heading">Subscription:</h3>

          <div className="product-popup-btns">
            {subscriptionOptions.map((item) => (
              <button
                key={item.value}
                className={`product-popup-btn ${
                  selectedSubscription === item.value ? "active" : ""
                }`}
                onClick={() => setSelectedSubscription(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {isAIS && (
        <div className="ProductPopupPage-section">
          <label className="ProductPopupPage-label">Location AIS Only:</label>

          <div className="ProductPopupPage-options">
            <select
              className="ProductPopupPage-selected"
              value={selectedState || ""}
              onChange={handleStateChange}
            >
              <option value="">Select State</option>
              {Object.keys(AIS140Availability).map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>

            <select
              className="ProductPopupPage-selected"
              value={selectedCity || ""}
              onChange={(e) => setSelectedCity(e.target.value)}
              disabled={!selectedState}
            >
              <option value="">Select City</option>
              {selectedState &&
                AIS140Availability[selectedState].map((city) => (
                  <option
                    key={city.name}
                    value={city.name}
                    disabled={!city.availability}
                  >
                    {city.name} {city.availability ? "" : "(Unavailable)"}
                  </option>
                ))}
            </select>
          </div>
        </div>
      )}

      <div className="ProductPopupPage-section">
        <label className="ProductPopupPage-label">Quantity:</label>

        <div className="ProductPopupPage-quantity-controls">
          <button
            onClick={() => setQuantity((prev) => (prev > 1 ? prev - 1 : 1))}
            className="ProductPopupPage-quantity-btn"
          >
            -
          </button>

          <span className="ProductPopupPage-quantity-value">{quantity}</span>

          <button
            onClick={() => setQuantity((prev) => prev + 1)}
            className="ProductPopupPage-quantity-btn"
          >
            +
          </button>
        </div>
      </div>

      <div className="product-popup-price-box">
        {pricingCalculation.minMRP < pricingCalculation.maxMRP && (
          <p className="product-overview-mrp-range">
            MRP Range: ₹{pricingCalculation.minMRP.toFixed(2)} – ₹
            {pricingCalculation.maxMRP.toFixed(2)}
          </p>
        )}

        <div className="product-popup-price-row">
          <span className="product-popup-final-price">
            ₹{pricingCalculation.singleItemDiscountedPrice.toFixed(2)}
          </span>

          <span className="product-popup-per-item">per item</span>

          {pricingCalculation.currentPrice > 0 && (
            <span className="product-popup-old-price">
              ₹{pricingCalculation.currentPrice.toFixed(2)}
            </span>
          )}

          {device?.discount > 0 && (
            <span className="product-popup-discount-badge">
              {device.discount}% OFF
            </span>
          )}
        </div>

        <p className="product-popup-save-text">
          You save ₹{pricingCalculation.savings.toFixed(2)}
          {device?.discount > 0 && ` (${device.discount}%)`}
        </p>

        {pricingCalculation.optionsTotal > 0 && (
          <p className="product-popup-addons">
            Add-ons: ₹{pricingCalculation.optionsTotal}
          </p>
        )}

        <p className="product-popup-quantity-info">
          {quantity} × ₹
          {pricingCalculation.singleItemDiscountedPrice.toFixed(2)}
        </p>

        <div className="product-popup-total">
          <span>Item Total: </span>
          <span className="product-popup-total-price">
            ₹{pricingCalculation.totalPrice.toFixed(2)}
          </span>
        </div>

        <p className="product-popup-tax-text">Inclusive of all taxes</p>

        <div className="product-overview-disclaimers">
          <div className="disclaimer-item">
            <FaTruck className="disclaimer-icon" />
            <span>Free shipping on orders above ₹3000 (min 2 items)</span>
          </div>

          <div className="disclaimer-item">
            <FaShieldAlt className="disclaimer-icon" />
            <span>1-year warranty (manufacturing defects only)</span>
          </div>
        </div>

        <div className="product-overview-trust">
          <span className="trust-item">
            Orders can’t be cancelled once confirmed after verification
          </span>
          <span className="trust-dot">•</span>
          <span className="trust-item">
            Use responsibly as per regulations
          </span>
        </div>
      </div>

      <div className="ProductPopupPage-actions">
        <button
          onClick={handleAddToCart}
          className="ProductPopupPage-add-cart-button"
          disabled={isBuyDisabled || isProcessing}
        >
          {isProductInCart ? "Update Product" : "Add to Cart"}
        </button>

        <button
          onClick={handleBuyNow}
          className="ProductPopupPage-buy-now-button"
          disabled={isBuyDisabled || isProcessing}
        >
          Buy it now
        </button>
      </div>
    </div>
  );
};

export default ProductPopupPage;










// import React, { useState, useContext, useEffect, useMemo } from "react";
// import { CartContext } from "../../contexts/CartContext";
// import { useNavigate } from "react-router-dom";
// import toast, { Toaster } from "react-hot-toast";
// import "./ProductPopupPage.css";
// import AIS140Availability from "../../contexts/AIS140Availabilities";
// import { FaTruck, FaUndo, FaExclamationCircle, FaShieldAlt } from "react-icons/fa";

// const ProductPopupPage = ({ device }) => {
//   const { cartItems, loading, addToCart, updateCartItem, cartLoaded } = useContext(CartContext);
//   const navigate = useNavigate();

//   const existingCartItem = cartItems.find(
//     (item) => Number(item?.device.id) === Number(device?.id)
//   );

//   const isProductInCart = !!existingCartItem;

//   const [selectedRelayer, setSelectedRelayer] = useState(null);
//   const [selectedNetwork, setSelectedNetwork] = useState(null);
//   const [selectedSubscription, setSelectedSubscription] = useState(null);
//   const [quantity, setQuantity] = useState(1);
//   const [selectedImage, setSelectedImage] = useState(device?.image?.[1] || "");

//   const [selectedState, setSelectedState] = useState(null);
//   const [selectedCity, setSelectedCity] = useState(null);
//   const [isAIS, setIsAIS] = useState(false);

//   useEffect(() => {
//     setIsAIS(device.name.includes("AIS"));

//     // Set default network based on device configuration (same logic as first component)
//     const { network2gAmt, network4gAmt } = device;
//     let defaultNetwork = null;
//     if (network2gAmt === 0) defaultNetwork = "2G";
//     else if (network4gAmt === 0) defaultNetwork = "4G";

//     setSelectedNetwork(defaultNetwork);
//   }, [device]);

//   useEffect(() => {
//     if (device?.image?.length) {
//       setSelectedImage(device.image[1]);
//     }
//   }, [device]);

//   useEffect(() => {
//     if (!cartLoaded || !device) return;

//     const cartItem = cartItems.find(
//       (item) => Number(item?.device?.id) === Number(device.id)
//     );

//     if (cartItem) {
//       setQuantity(cartItem.quantity || 1);
//       setSelectedNetwork(cartItem.network || null);
//       // setSelectedRelayer(cartItem.relayer || null);
//       setSelectedRelayer(cartItem.isRelay ? device.relayAmt : null);
//       setSelectedSubscription(cartItem.subscription || null);
//       setSelectedState(cartItem.state || null);
//       setSelectedCity(cartItem.city || null);
//     }
//   }, [cartLoaded, cartItems, device]);

//   useEffect(() => {
//     if (!device) return;

//     // AIS
//     setIsAIS(device.name.includes("AIS"));

//     // NETWORK
//     const has4G = Number(device.network4gAmt) > 0;
//     const has2G = Number(device.network2gAmt) > 0;

//     if (has4G) setSelectedNetwork("4G");
//     else if (has2G) setSelectedNetwork("2G");
//     else setSelectedNetwork("2G");

//     // RELAYER
//     if (device.isRelay) {
//       if (device.relayAmt === 0) {
//         setSelectedRelayer("included");
//       } else {
//         setSelectedRelayer("without");
//       }
//     }

//     // SUBSCRIPTION
//     if (device.isSubscription) {
//       if (device.subscriptionYearlyAmt === 0) {
//         setSelectedSubscription("yearly");
//       } else {
//         setSelectedSubscription("monthly");
//       }
//     }
//   }, [device]);

//   const selectedCityInfo = AIS140Availability[selectedState]?.find(
//     (city) => city.name === selectedCity
//   );

//   const isBuyDisabled = isAIS
//     ? !selectedState || !selectedCity || !selectedCityInfo?.availability
//     : false;

//   const TAX_RATE = 0.18; // 18%

//   const round2 = (n) => Math.round(n * 100) / 100;

//   const pricingCalculation = useMemo(() => {
//     if (!device) {
//       return {
//         discountedPrice: 0,
//         totalPrice: 0,
//         savings: 0,
//         currentPrice: 0,
//         minMRP: 0,
//         maxMRP: 0,
//         discountPercent: 0,
//         tax: 0,
//         baseAmount: 0,
//       };
//     }

//     const gstPercent = Number(device.gstPercent || 18);

//     // ===== CURRENT PRICE (without discount, with selected options) =====
//     let currentBase = Number(device.amount || 0);

//     if (selectedNetwork === "2G") {
//       currentBase += Number(device.network2gAmt || 0);
//     }

//     if (selectedNetwork === "4G") {
//       currentBase += Number(device.network4gAmt || 0);
//     }

//     if (selectedSubscription === "monthly") {
//       currentBase += Number(device.subscriptionMonthlyAmt || 0);
//     }

//     if (selectedSubscription === "yearly") {
//       currentBase += Number(device.subscriptionYearlyAmt || 0);
//     }

//     if (selectedRelayer) {
//       currentBase += Number(device.relayAmt || 0);
//     }

//     currentBase = round2(currentBase);

//     const currentTax = round2(
//       currentBase * (gstPercent / 100)
//     );

//     const currentPrice = round2(
//       currentBase + currentTax
//     );

//     // ===== DISCOUNTED PRICE (EXACTLY SAME AS BACKEND) =====
//     let discountedBase = Number(device.amount || 0);

//     const discount = Number(device.discount || 0);

//     if (discount > 0) {
//       discountedBase =
//         discountedBase * (1 - discount / 100);
//     }

//     if (selectedNetwork === "2G") {
//       discountedBase += Number(device.network2gAmt || 0);
//     }

//     if (selectedNetwork === "4G") {
//       discountedBase += Number(device.network4gAmt || 0);
//     }

//     if (selectedSubscription === "monthly") {
//       discountedBase += Number(
//         device.subscriptionMonthlyAmt || 0
//       );
//     }

//     if (selectedSubscription === "yearly") {
//       discountedBase += Number(
//         device.subscriptionYearlyAmt || 0
//       );
//     }

//     if (selectedRelayer) {
//       discountedBase += Number(device.relayAmt || 0);
//     }

//     discountedBase = round2(discountedBase);

//     const tax = round2(
//       discountedBase * (gstPercent / 100)
//     );

//     const discountedPrice = round2(
//       discountedBase + tax
//     );

//     const totalPrice = round2(
//       discountedPrice * quantity
//     );

//     // ===== MRP RANGE =====
//     const minMRP = round2(
//       round2(Number(device.amount || 0)) +
//       round2(
//         Number(device.amount || 0) *
//         (gstPercent / 100)
//       )
//     );

//     const maxBase =
//       Number(device.amount || 0) +
//       Number(device.network2gAmt || 0) +
//       Number(device.network4gAmt || 0) +
//       Number(device.relayAmt || 0) +
//       Number(device.subscriptionMonthlyAmt || 0) +
//       Number(device.subscriptionYearlyAmt || 0);

//     const maxMRP = round2(
//       round2(maxBase) +
//       round2(maxBase * (gstPercent / 100))
//     );

//     return {
//       discountedPrice,
//       totalPrice,
//       savings: round2(
//         currentPrice - discountedPrice
//       ),
//       currentPrice,
//       minMRP,
//       maxMRP,
//       discountPercent: discount,
//       tax,
//       baseAmount: discountedBase,
//     };
//   }, [
//     device,
//     selectedNetwork,
//     selectedRelayer,
//     selectedSubscription,
//     quantity,
//   ]);

//   const pricing = pricingCalculation

//   const handleStateChange = (e) => {
//     setSelectedState(e.target.value);
//     setSelectedCity("");
//   };

//   const handleCityChange = (e) => setSelectedCity(e.target.value);



//   const getFinalPrice = () => {
//     let price = Math.round(
//       (device.amount || 100) * (1 - (device.discount || 0) / 100)
//     );

//     if (selectedRelayer) price += parseInt(selectedRelayer) || 0;

//     if (selectedNetwork === "2G") {
//       price += parseInt(device.network2gAmt) || 0;
//     } else if (selectedNetwork === "4G") {
//       price += parseInt(device.network4gAmt) || 0;
//     }

//     if (selectedSubscription === "monthly") {
//       price += parseInt(device.subscriptionMonthlyAmt) || 0;
//     } else if (selectedSubscription === "yearly") {
//       price += parseInt(device.subscriptionYearlyAmt) || 0;
//     }

//     if (isAIS && selectedCityInfo?.price) {
//       price += parseInt(selectedCityInfo.price) || 0;
//     }

//     return price;
//   };

//   const calculateTotalAmount = () => {
//     let price = Math.round(
//       (device.amount || 100) * (1 - (device.discount || 0) / 100)
//     );

//     if (selectedRelayer) price += parseInt(selectedRelayer) || 0;

//     if (selectedNetwork === "2G") {
//       price += parseInt(device.network2gAmt) || 0;
//     } else if (selectedNetwork === "4G") {
//       price += parseInt(device.network4gAmt) || 0;
//     }

//     if (selectedSubscription === "monthly") {
//       price += parseInt(device.subscriptionMonthlyAmt) || 0;
//     } else if (selectedSubscription === "yearly") {
//       price += parseInt(device.subscriptionYearlyAmt) || 0;
//     }

//     if (isAIS && selectedCityInfo?.price) {
//       price += parseInt(selectedCityInfo.price) || 0;
//     }

//     return price * quantity;
//   };

//   const calculatePricing = () => {
//     const originalBase = Number(device.amount) || 0;
//     const discountPercent = Number(device.discount) || 0;

//     const discountedBase =
//       originalBase - (originalBase * discountPercent) / 100;

//     let optionsTotal = 0;

//     if (selectedRelayer) {
//       optionsTotal += Number(device.relayAmt) || 0;
//     }

//     if (selectedNetwork === "2G") {
//       optionsTotal += Number(device.network2gAmt) || 0;
//     } else if (selectedNetwork === "4G") {
//       optionsTotal += Number(device.network4gAmt) || 0;
//     }

//     if (selectedSubscription === "monthly") {
//       optionsTotal += Number(device.subscriptionMonthlyAmt) || 0;
//     } else if (selectedSubscription === "yearly") {
//       optionsTotal += Number(device.subscriptionYearlyAmt) || 0;
//     }

//     if (isAIS && selectedCityInfo?.price) {
//       optionsTotal += Number(selectedCityInfo.price) || 0;
//     }

//     const displayAmount = originalBase + optionsTotal;

//     const unitFinalPrice = discountedBase + optionsTotal;

//     const totalPrice = unitFinalPrice * quantity;

//     return {
//       originalBase,
//       displayAmount,
//       discountedBase,
//       unitFinalPrice,
//       totalPrice,
//     };
//   };

//   const handleAddToCart = async () => {
//     if (!device) return;

//     if (isAIS && (!selectedState || !selectedCity)) {
//       toast.error("Please select your state and city");
//       return;
//     }

//     const allNetworkZeroOrExcluded =
//       (device.network2gAmt === undefined || device.network2gAmt === -1) &&
//       (device.network4gAmt === undefined || device.network4gAmt === -1);

//     const allSubscriptionZero =
//       (!device.subscriptionMonthlyAmt || device.subscriptionMonthlyAmt === 0) &&
//       (!device.subscriptionYearlyAmt || device.subscriptionYearlyAmt === 0);

//     if (device.isNetwork && !allNetworkZeroOrExcluded && !selectedNetwork) {
//       toast.error("Please select a network option before adding to cart");
//       return;
//     }

//     if (device.isSubscription && !allSubscriptionZero && !selectedSubscription) {
//       toast.error("Please select a subscription option before adding to cart");
//       return;
//     }

//     const totalAmount = pricing.totalPrice;

//     const cartData = {
//       deviceId: device.id,
//       product: { selectedDevice: device },
//       quantity,
//       totalAmount,
//       // price: totalAmount / quantity,
//       price: pricing.unitFinalPrice,
//       name: device.name,
//       model: device.model,
//       discount: device.discount || 0,
//       network: selectedNetwork,
//       isRelay: !!selectedRelayer,
//       relayer: selectedRelayer,
//       subscription: selectedSubscription,
//       state: selectedState,
//       city: selectedCity,
//     };

//     try {
//       if (isProductInCart) {
//         await updateCartItem(existingCartItem, cartData);
//         // toast.success("Product updated in cart!");
//       } else {
//         await addToCart(cartData);
//         // toast.success("Product added to cart!");
//       }
//     } catch (error) {
//       toast.error("Something went wrong");
//     }
//   };

//   const handleBuyNow = () => {
//     // First validate all required selections
//     if (isAIS && (!selectedState || !selectedCity)) {
//       toast.error("Please select your state and city");
//       return;
//     }

//     // Check if network selection is required
//     const allNetworkZeroOrExcluded =
//       (device.network2gAmt === undefined || device.network2gAmt === -1) &&
//       (device.network4gAmt === undefined || device.network4gAmt === -1);

//     const allSubscriptionZero =
//       (!device.subscriptionMonthlyAmt || device.subscriptionMonthlyAmt === 0) &&
//       (!device.subscriptionYearlyAmt || device.subscriptionYearlyAmt === 0);

//     if (device.isNetwork && !allNetworkZeroOrExcluded && !selectedNetwork) {
//       toast.error("Please select a network option before adding to cart");
//       return;
//     }

//     if (device.isSubscription && !allSubscriptionZero && !selectedSubscription) {
//       toast.error("Please select a subscription option before adding to cart");
//       return;
//     }

//     // If all validations pass, add to cart and navigate
//     handleAddToCart();
//     navigate("/cart");
//   };


//   return (
//     <div className="ProductPopupPage-container">
 
//       <div className="ProductPopupPage-header">


//         <div className="ProductPopupPage-image-wrapper">
//           <img
//             src={selectedImage || "/images/placeholder-product.png"}
//             alt={device.name}
//             className="ProductPopupPage-main-image"
//             onError={(e) => (e.target.src = "/images/placeholder-product.png")}
//           />

//           <div className="ProductPopupPage-thumbnails">
//             {device?.image?.slice(1).map((img, index) => (
//               <img
//                 key={index}
//                 src={img}
//                 alt={`thumb-${index + 1}`}
//                 className={`ProductPopupPage-thumb ${selectedImage === img ? "active" : ""
//                   }`}
//                 onClick={() => setSelectedImage(img)}
//               />
//             ))}
//           </div>

//         </div>
//         <div className="ProductPopupPage-details">
//           <h2 className="ProductPopupPage-title">{device.name}</h2>
//           <p className="ProductPopupPage-subtitle">{device.description}</p>

//         </div>
//       </div>


//       {device?.isRelay && (
//         <div className="product-popup-extra-card">
//           <h3 className="product-popup-heading">Relayer Option:</h3>

//           <div className="product-popup-btns">
//             {device.relayAmt === 0 ? (
//               <button className="product-popup-btn active">
//                 Relay - Included
//               </button>
//             ) : (
//               <>
//                 <button
//                   className={`product-popup-btn ${selectedRelayer === "without" ? "active" : ""
//                     }`}
//                   onClick={() => setSelectedRelayer("without")}
//                 >
//                   Without Relay
//                 </button>

//                 <button
//                   className={`product-popup-btn ${selectedRelayer === "with" ? "active" : ""
//                     }`}
//                   onClick={() => setSelectedRelayer("with")}
//                 >
//                   Relay - Included
//                 </button>
//               </>
//             )}
//           </div>
//         </div>
//       )}

//       {device?.isNetwork && (
//         <div className="product-popup-extra-card">
//           <h3 className="product-popup-heading">Network:</h3>

//           <div className="product-popup-btns">
//             <button className="product-popup-btn active">Airtel</button>
//           </div>

//           {(device.network2gAmt === 0 && device.network4gAmt === 0) ? (
//             <p className="product-popup-note">2G & 4G Included</p>
//           ) : (
//             <p className="product-popup-note">
//               Network charges applied based on plan
//             </p>
//           )}
//         </div>
//       )}

//       {device?.isSubscription && (
//         <div className="product-popup-extra-card">
//           <h3 className="product-popup-heading">Subscription:</h3>

//           <div className="product-popup-btns">
//             {device.subscriptionYearlyAmt === 0 ? (
//               <button className="product-popup-btn active">
//                 1 year subscription included
//               </button>
//             ) : (
//               <>
//                 <button
//                   className={`product-popup-btn ${selectedSubscription === "monthly" ? "active" : ""
//                     }`}
//                   onClick={() => setSelectedSubscription("monthly")}
//                 >
//                   1 month subscription
//                 </button>

//                 <button
//                   className={`product-popup-btn ${selectedSubscription === "yearly" ? "active" : ""
//                     }`}
//                   onClick={() => setSelectedSubscription("yearly")}
//                 >
//                   1 year subscription
//                 </button>
//               </>
//             )}
//           </div>

//         </div>
//       )}

//       {/* === LOCATION (AIS ONLY) === */}
//       {isAIS && (
//         <div className="ProductPopupPage-section">
//           <label className="ProductPopupPage-label">Location (AIS Only):</label>
//           <div className="ProductPopupPage-options">
//             <select
//               id="ais-state"
//               name="aisState"
//               className="ProductPopupPage-selected"
//               value={selectedState || ""}
//               onChange={handleStateChange}
//             >
//               <option value="">Select State</option>
//               {Object.keys(AIS140Availability).map((state) => (
//                 <option key={state} value={state}>
//                   {state}
//                 </option>
//               ))}
//             </select>

//             <select
//               id="ais-city"
//               name="aisCity"
//               className="ProductPopupPage-selected"
//               value={selectedCity || ""}
//               onChange={handleCityChange}
//               disabled={!selectedState}
//             >
//               <option value="">Select City</option>
//               {selectedState &&
//                 AIS140Availability[selectedState].map((city) => (
//                   <option
//                     key={city.name}
//                     value={city.name}
//                     disabled={!city.availability}
//                   >
//                     {city.name} {city.availability ? "" : "(Unavailable)"}
//                   </option>
//                 ))}
//             </select>
//           </div>

//         </div>
//       )}

//       {/* === QUANTITY === */}
//       <div className="ProductPopupPage-section">
//         <label className="ProductPopupPage-label">Quantity:</label>
//         <div className="ProductPopupPage-quantity-controls">
//           <button
//             onClick={() => setQuantity((prev) => (prev > 1 ? prev - 1 : 1))}
//             className="ProductPopupPage-quantity-btn"
//           >
//             -
//           </button>
//           <span className="ProductPopupPage-quantity-value">{quantity}</span>
//           <button
//             onClick={() => setQuantity((prev) => prev + 1)}
//             className="ProductPopupPage-quantity-btn"
//           >
//             +
//           </button>
//         </div>
//       </div>

//       <div className="product-popup-price-box">
//         {pricingCalculation.minMRP < pricingCalculation.maxMRP && (
//           <p className="product-overview-mrp-range">
//             MRP Range: ₹{pricingCalculation.minMRP.toFixed(2)} – ₹{pricingCalculation.maxMRP.toFixed(2)}
//           </p>
//         )}

//         {/* 🔥 MAIN PRICE */}
//         <div className="product-popup-price-row">
//           <span className="product-popup-final-price">
//             ₹{pricingCalculation.discountedPrice.toFixed(2)}
//           </span>
//           <span className="product-popup-per-item">per item</span>

//           {pricingCalculation.currentPrice > 0 && (
//             <span className="product-popup-old-price">
//               ₹{pricingCalculation.currentPrice.toFixed(2)}
//             </span>
//           )}

//           {device?.discount > 0 && (
//             <span className="product-popup-discount-badge">
//               {device.discount}% OFF
//             </span>
//           )}
//         </div>

//         {/* 💰 SAVINGS */}
//         <p className="product-popup-save-text">
//           You save ₹{pricingCalculation.savings.toFixed(2)}
//           {device?.discount > 0 && ` (${device.discount}%)`}
//         </p>

//         {/* 🧩 ADD-ONS */}
//         {pricingCalculation.optionsTotal > 0 && (
//           <p className="product-popup-addons">
//             Add-ons: ₹{pricingCalculation.optionsTotal}
//           </p>
//         )}

//         {/* 📦 QUANTITY */}
//         <p className="product-popup-quantity-info">
//           {quantity} × ₹{pricingCalculation.discountedPrice.toFixed(2)}
//         </p>

//         {/* 💳 TOTAL */}
//         <div className="product-popup-total">
//           <span>Item Total: </span>
//           <span className="product-popup-total-price">
//             ₹{pricingCalculation.totalPrice.toFixed(2)}
//           </span>
//         </div>

//         {/* 🧾 TAX */}
//         <p className="product-popup-tax-text">
//           Inclusive of all taxes
//         </p>

//         <div className="product-overview-disclaimers">
//           <div className="disclaimer-item">
//             <FaTruck className="disclaimer-icon" />
//             <span>Free shipping on orders above ₹3000 (min 2 items)</span>
//           </div>
//           <div className="disclaimer-item">
//             <FaShieldAlt className="disclaimer-icon" />
//             <span>1-year warranty (manufacturing defects only)</span>
//           </div>

//         </div>

//         <div className="product-overview-trust">
//           <span className="trust-item">Orders can’t be cancelled once confirmed after verification</span>

//           <span className="trust-dot">•</span>
//           <span className="trust-item"> Use responsibly as per regulations</span>
//         </div>

//       </div>

//       {/* === ACTIONS === */}
//       <div className="ProductPopupPage-actions">
//         <button
//           onClick={handleAddToCart}
//           className="ProductPopupPage-add-cart-button"
//           disabled={isBuyDisabled}
//         >
//           {isProductInCart ? "Update Product" : "Add to Cart"}
//         </button>
//         <button
//           onClick={handleBuyNow}
//           className="ProductPopupPage-buy-now-button"
//           disabled={isBuyDisabled}
//         >
//           Buy it now
//         </button>
//       </div>
//     </div>
//   );
// };

// export default ProductPopupPage;